import { IDynamicLinkedServiceTypeMetadata, ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { format } from 'app/shared/utils/string.util';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { ISelectOption } from 'app/interfaces/ISelectOption';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { parameterizeDynamicsControlMetadata } from '../consts/metadata-consts';
import { GlobalVariables } from 'app/services/globals';
import { driverVersions, driverVersionVersionOptions } from '../consts/tabular-consts';
import { SSLMode, sslModeOptions, teradataAdditionalFieldsKeyOptions } from '../consts/teradata-consts';
import { ConnectionAuthKind, Metamodel } from 'shell-models-lib';
import { TeradataSink } from 'app/model/pipeline/copysink/teradata.copysink';
import { BuiltInDataSourceOptionId, DataSourceOptionKind } from '@powerquery/mashup-editor-client';
import { ExposureControlService } from 'app/services/exposure-control/base-exposure-control.service';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';

export const serverControl: ControlMetadata = {
    type: 'textbox',
    bind: 'server',
    label: stringResources.ModelName_ServerName,
    required: true,
    validators: [
        { type: 'required', errorMessage: '' }
    ],
    isLabelVertical: true
};

export const userNameControl: ControlMetadata = {
    type: 'textbox',
    bind: 'userName',
    label: stringResources.ModelName_UserName,
    required: true,
    validators: [
        { type: 'required', errorMessage: '' }
    ],
    isLabelVertical: true
};

export const passwordControl: ControlMetadata = {
    type: 'dynamic-akv-switcher',
    bind: 'password',
    label: stringResources.ModelName_Password,
    child: {
        type: 'password',
        label: stringResources.ModelName_Password,
        required: true,
        isLabelVertical: true
    },
    validators: [
        { type: 'required', errorMessage: '' }
    ],
    description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
        format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Teradata_DisplayText),
        stringResources.ModelName_Password)
};

export const sslModeControl: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'select',
        bind: 'sslMode',
        label: stringResources.SSL_Mode,
        options: sslModeOptions,
        isLabelVertical: true,
        defaultValue: SSLMode.Prefer
    }
};

const teradataV1LinkedServiceControl: ControlMetadata[] = [
    parameterizeDynamicsControlMetadata(serverControl),
    {
        type: 'select',
        bind: 'authenticationType',
        label: stringResources.ModelName_AuthenticationType,
        required: true,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        defaultValue: 'Basic',
        isLabelVertical: true,
        options: '{ isOldMashupResource ? authOptions : authOptionsWithLDAP }' as unknown as Array<ISelectOption<string>>
    },
    parameterizeDynamicsControlMetadata(userNameControl),
    parameterizeDynamicsControlMetadata(passwordControl),
    {
        type: 'key-val-list',
        bind: 'additionalFields',
        label: stringResources.AdditionalConnectionStringFields,
        hide: () => {
            return false;
        }
    }
];

const v2Controls: ControlMetadata[] = [
    parameterizeDynamicsControlMetadata(serverControl),
    {
        type: 'select',
        bind: 'authenticationType',
        label: stringResources.ModelName_AuthenticationType,
        required: true,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        defaultValue: 'Basic',
        isLabelVertical: true,
        options: '{ authOptionsWithLDAP }' as unknown as Array<ISelectOption<string>>
    },
    parameterizeDynamicsControlMetadata(userNameControl),
    parameterizeDynamicsControlMetadata(passwordControl),
    sslModeControl,
    {
        type: 'parameter-config',
        bind: 'parameterizableAdditionalFields',
        label: stringResources.AdditionalConnectionStringFields,
        isLayoutVertical: true,
        isKeyDropdown: true,
        keyOptions: teradataAdditionalFieldsKeyOptions,
        keyOptionHasType: true,
        dynamicInputTypes: true,
        dynamicInputTypesParameterizable: true,
        keyLabel: stringResources.PropertyName,
        defaultWidth: 250
    }
];

export const teradataMetadata: IDynamicLinkedServiceTypeMetadata = async (injector) => {
    const exposureControlService = await getServiceInstance<ExposureControlService>(
        injector,
        'ExposureControlService'
    );
    const metadata: ILinkedServiceTypeMetadata = {
        type: adfModelTypeMap.teradata.linkedServiceType,
        displayName: stringResources.Teradata_DisplayText,
        linkedServiceKind: 'table',
        icon: {
            className: 'svg-i-100x100-Teradata'
        },
        isDeprecated: GlobalVariables.showLegacy ? undefined : '{ version !== "v2" }',
        showWarningMessage: '{ version !== "v2" }',
        warningMessage: format(
            stringResources.Connector_Legacy_IsNotSupportedWarning_alt,
            stringResources.VersionLabel.toLocaleLowerCase()
        ),
        warningMessageHyperlink: 'https://learn.microsoft.com/azure/data-factory/connector-teradata',
        supportedInTrident: true,
        supportedAuthKindsInTrident: [ConnectionAuthKind.Basic, ConnectionAuthKind.Windows],
        notSupportFabricBuiltInStagingWhenSink: !GlobalVariables.enableTeradataBuiltIn,
        pqoDataSourceOption: {
            kind: DataSourceOptionKind.BuiltIn,
            id: BuiltInDataSourceOptionId.Teradata
        },
        supportAutoFillCopilotInput: true,
        isKnownInPipelineContext: true,
        supportCopyJobSource: true,
        supportCopyJobSink: true,
        supportMultipleIncrementalColumns: true,
        supportOPDG: GlobalVariables.enableOPDG,
        isFirstClass: true,
        supportFirstClassParameterExperience: true,
        fetchTableNameFromItemPath: true,
        supportDatasetV2: true,
        tableColumnNameQuotes: ['"', '"'],
        // eslint-disable-next-line max-len
        timedQueryTemplate: 'select * from <<tableName>> \nwhere <<columnName>> >= \'@{formatDateTime(pipeline().parameters.windowStart, \'yyyy-MM-ddTHH:mm:ss\' )}\' \nAND <<columnName>> < \'@{formatDateTime(pipeline().parameters.windowEnd, \'yyyy-MM-ddTHH:mm:ss\' )}\'',
        copySourceTypes: [adfModelTypeMap.teradata.copySourceType],
        copySinkTypes: () => (__IS_FABRIC_DPE__ || exposureControlService.enableTeradataSinkInADF) ? [Metamodel.modelFor(TeradataSink).name] : [],
        category: ['Database'],
        SHIRVersion: {
            v2: '5.51.9175.1'
        },
        controls: [
            {
                type: 'radio-switch',
                bind: 'version',
                label: stringResources.VersionLabel,
                isLabelVertical: true,
                options: driverVersionVersionOptions,
                defaultValue: driverVersions.v2,
                hide: GlobalVariables.showLegacy ? false : '{version === "v2" }',
                cases: [
                    {
                        caseValue: driverVersions.v1,
                        children: [
                            {
                                type: 'hint',
                                hintMode: 'warning',
                                description: format(
                                    stringResources.Connector_Legacy_IsNotSupportedWarning_alt6,
                                    stringResources.VersionLabel.toLocaleLowerCase()),
                                link: 'https://learn.microsoft.com/azure/data-factory/connector-teradata'
                            },
                            ...teradataV1LinkedServiceControl
                        ]
                    },
                    {
                        caseValue: driverVersions.v2,
                        children: v2Controls
                    }
                ]
            },
            ...(v2Controls
                .map((control) => ({
                    ...control,
                    hide: GlobalVariables.showLegacy ? true : '{version !== "v2" }'
                }))
            )
        ]
    };

    return metadata;
};
